import { useEffect, useMemo, useState } from 'react';
import { shareSummary } from '../lib/share.js';
import { renderShareImage } from '../lib/share-image.js';
import { toISODay, type BodyProfile, type DayRow, type Goal } from '../lib/stats.js';
import type { Theme } from '../lib/theme.js';
import { useEscape } from '../lib/useEscape.js';

interface Props {
  series: DayRow[];
  profile: BodyProfile;
  goal: Goal;
  /** Fenêtre de la carte Tendance : l'image annonce la pente que l'écran affiche. */
  trendWindow: number;
  /** Thème de l'écran, proposé par défaut pour l'image. */
  theme: Theme;
  onClose: () => void;
}

interface Rendered {
  file: File;
  url: string;
}

/**
 * Le partage de fichiers n'existe pas partout : Chrome et Edge le proposent sur
 * Android comme sous Windows, Safari aussi ; Firefox et Chrome sous Linux non.
 * Sans lui, il reste le téléchargement.
 */
function canShareFile(file: File): boolean {
  try {
    return typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] });
  } catch {
    return false;
  }
}

/**
 * Aperçu puis partage. L'image est produite dès l'ouverture, et non au clic sur
 * « Partager » : la feuille de partage exige un geste récent de l'utilisateur,
 * qu'une génération de quelques centaines de millisecondes suffirait à faire
 * expirer sur un téléphone lent.
 */
export function ShareModal({ series, profile, goal, trendWindow, theme, onClose }: Props) {
  useEscape(onClose);

  const summary = useMemo(
    () => shareSummary(series, profile, goal, trendWindow),
    [series, profile, goal, trendWindow],
  );
  const [look, setLook] = useState<Theme>(theme);
  const [image, setImage] = useState<Rendered | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shareFailed, setShareFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setImage(null);
    setError(null);

    void renderShareImage(summary, look)
      .then((blob) => {
        if (cancelled) return;
        const file = new File([blob], `ephemeris-${toISODay(summary.to)}.png`, {
          type: 'image/png',
        });
        url = URL.createObjectURL(file);
        setImage({ file, url });
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : 'Image impossible à produire.');
        }
      });

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [summary, look]);

  // Avant que l'image existe, on suppose que l'API tiendra parole : sinon le
  // bouton apparaîtrait sous le doigt une fois la génération terminée.
  const shareable = image ? canShareFile(image.file) : typeof navigator.canShare === 'function';
  // Un seul geste : la feuille de partage du système, qui propose déjà les
  // applications, la galerie ou l'enregistrement. Le téléchargement n'est
  // qu'un repli, là où elle n'existe pas ou quand elle a échoué.
  const offerDownload = !shareable || shareFailed;

  async function share() {
    if (!image) return;
    setError(null);
    try {
      await navigator.share({ files: [image.file] });
    } catch (cause) {
      // Refermer la feuille de partage sans choisir d'application n'est pas
      // une erreur.
      if (cause instanceof DOMException && cause.name === 'AbortError') return;
      setShareFailed(true);
      setError('Le partage n’a pas abouti. L’image reste téléchargeable ci-dessous.');
    }
  }

  function download() {
    if (!image) return;
    const link = document.createElement('a');
    link.href = image.url;
    link.download = image.file.name;
    link.click();
  }

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      aria-label="Partager ma semaine"
      onClick={onClose}
    >
      {/* Le clic sur le fond ferme ; celui sur la boîte ne doit pas remonter. */}
      <div className="modal__box share" onClick={(event) => event.stopPropagation()}>
        <header className="modal__head">
          <div>
            <div className="eyebrow">Partager</div>
            <h2 className="modal__title">Ma semaine en image</h2>
          </div>
          <button type="button" className="btn--icon" onClick={onClose} aria-label="Fermer">
            ✕
          </button>
        </header>

        <div className="share__frame">
          {image ? (
            <img
              className="share__preview"
              src={image.url}
              alt="Aperçu : poids moyen, pas et calories de la semaine, tendance, puis le détail jour par jour."
            />
          ) : (
            <div className="share__placeholder">
              <p className={error ? 'error' : 'hint'} role={error ? 'alert' : undefined}>
                {error ?? 'Génération de l’image…'}
              </p>
            </div>
          )}
        </div>

        <div className="share__controls">
          <div className="segmented" role="group" aria-label="Thème de l’image">
            <button type="button" aria-pressed={look === 'light'} onClick={() => setLook('light')}>
              Clair
            </button>
            <button type="button" aria-pressed={look === 'dark'} onClick={() => setLook('dark')}>
              Sombre
            </button>
          </div>
          <span className="hint mono">1080 × 1920 · story</span>
        </div>

        {error && image ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="share__actions">
          {shareable ? (
            <button type="button" className="btn" disabled={!image} onClick={() => void share()}>
              Partager
            </button>
          ) : null}
          {offerDownload ? (
            <button
              type="button"
              className={shareable ? 'btn btn--ghost' : 'btn'}
              disabled={!image}
              onClick={download}
            >
              Télécharger
            </button>
          ) : null}
        </div>

        <p className="hint">
          L’image est produite sur cet appareil : rien ne passe par le serveur, et ni votre nom ni
          votre adresse n’y figurent.
        </p>
      </div>
    </div>
  );
}
